import * as Markdownit from 'markdown-it';
import * as React from 'react';

import { CheckBoxWithLabelWidget } from '@tableau/widgets';
import { FormikProps, withFormik } from 'formik';
import { Messages } from '../../../codegen/Localize';
import { CustomizationSettingsCtrlProps } from '../controllers/CustomizationSettingsCtrl';
import { SettingsWelcomeChannelStyles } from '../styles/SettingsWelcomeChannelStyles';
import { htmlToMarkdown } from '../utils/MarkdownHtml';
import { FooterEditor } from './FooterEditor';
import { WelcomeChannelNavigationDialog } from './WelcomeChannelNavigationDialog';

export interface CustomizationSettingsValues {
  defaultBannerCheckbox: boolean;
  serverFooterCheckbox: boolean;
  serverFooter: {
    html: string;
    isValid: boolean;  // Validation comes from the subComponent
  };
}

export type InnerCustomizationSettingsProps = CustomizationSettingsCtrlProps & FormikProps<CustomizationSettingsValues> & CustomizationSettingsValues;

interface CustomizationSettingsState {
  isBusy: boolean;
  showNavigationDialog: boolean;
  navigationCallBack: () => void;
}

/**
 * Nit: Eventually this should be refactored to be a WelcomeChannelCustomizationForm component.
 * When there are more forms on the customization page, that refactor should happen.
 */
export class InnerCustomizationSettings extends React.PureComponent<InnerCustomizationSettingsProps, CustomizationSettingsState> {

  constructor(props: InnerCustomizationSettingsProps) {
    super(props);
    props.setSaveCallback(props.handleSubmit);
    props.setCancelCallback(props.handleReset);
    props.setOpenNavigationDialogCallback(this.handleShowNavigationDialog);
    props.setIsBusy(() => props.isSubmitting);

    this.state = {
      isBusy: false,
      navigationCallBack: null,
      showNavigationDialog: false,
    };
  }

  public componentDidUpdate() {
    // Let the Angular controller know things have changed
    this.props.setIsFormDirty(this.props.dirty);
    this.props.setIsFormValid(this.props.isValid);
    this.props.setIsBusy(() => this.props.isSubmitting);
  }

  private handleChangeAdapter = (value: any, fieldName: keyof CustomizationSettingsValues) => {
    const event = {
      target: {
        name: fieldName,
        value
      }
    };
    // Call the Formik handleChange
    this.props.handleChange(event);
  }

  render() {
    return(
      <section>
        <div className="tb-settings-group-name">{Messages.settings_welcomeChannel_title()}</div>
        <div className="tb-indent">
          <p style={SettingsWelcomeChannelStyles.settingsDescription}>{Messages.settings_welcomeChannel_description()}</p>

          <CheckBoxWithLabelWidget
            checked={this.props.values.defaultBannerCheckbox}
            handleChange={(isBoxChecked: boolean) => this.handleChangeAdapter(isBoxChecked, 'defaultBannerCheckbox')}
            testId="defaultBanner"
            label={Messages.settings_welcomeChannel_defaultBannerCheckboxLabel()} />

          <CheckBoxWithLabelWidget
            checked={this.props.values.serverFooterCheckbox}
            handleChange={(isBoxChecked: boolean) => this.handleChangeAdapter(isBoxChecked, 'serverFooterCheckbox')}
            testId="customServerFooter"
            label={Messages.settings_welcomeChannel_customServerFooterCheckboxLabel()} />

          {this.props.values.serverFooterCheckbox && <FooterEditor
            formName={'ServerFooter-temp'}
            value={this.props.values.serverFooter.html}
            onChange={(html: string, isValid: boolean) => this.handleChangeAdapter({
              html,
              isValid
            }, 'serverFooter')}
            showDefaultWelcomeChannel={this.props.values.defaultBannerCheckbox}
          />}
          {this.state.showNavigationDialog && <WelcomeChannelNavigationDialog
            handleClose={this.handleHideNavigationDialog}
            handleNavigation={this.state.navigationCallBack}
            handleSave={this.props.submitForm}
          />}
        </div>
      </section>
    );
  }

  private handleShowNavigationDialog = (successCallback: () => void) => {
    this.setState({
      showNavigationDialog: true,
      navigationCallBack: successCallback
    });
  }

  private handleHideNavigationDialog = () => {
    this.setState({
      showNavigationDialog: false
    });
  }
}

export const CustomizationSettings = withFormik<CustomizationSettingsCtrlProps, CustomizationSettingsValues>({
  enableReinitialize: true,   // Reset the form when initial props change
  mapPropsToValues: props => {
    const initInfo = props.initialServerWelcomeChannelInfo;
    return {
      defaultBannerCheckbox: !!(initInfo && initInfo.welcomeChannelEnabledForServer),
      serverFooterCheckbox: !!(initInfo && initInfo.welcomeChannelServerFooter),
      serverFooter: {
        html: initInfo ? convertMarkdownToHtml(initInfo.welcomeChannelServerFooter) : '',
        isValid: true // Assumes that previously saved value is valid
      }
    };
  },
  validate: (values, props) => {
    const errors: any = {};
    if (!values.serverFooter.isValid) {
      errors.serverFooter = 'server not valid';
    }
    return errors;
  },
  handleSubmit: (values, actions) => {
    const serverFooterMarkdown = values.serverFooterCheckbox ? htmlToMarkdown(values.serverFooter.html) : '';
    return actions.props.sendUpdateRequest(values.defaultBannerCheckbox, serverFooterMarkdown).then(() => {
      // Reset the form after submission happens
      actions.setSubmitting(false);
      actions.resetForm({values});
    });
  }
})(InnerCustomizationSettings);

function convertMarkdownToHtml(markdown: string): string {
  return new Markdownit().render(markdown);
}
